/** ORDER_HUD_REFUSED_ROWS_ON_FACE_1 R1: absence is never evidence of zero drops. */
export type RefusedRows =
  | { state: "UNSERVED" }
  | { state: "NONE" }
  | { state: "REFUSED"; total: number; items: { field: string; reason: string; count: number }[] };

export function readRefusedRows(body: unknown): RefusedRows {
  if (!body || typeof body !== "object" || !Object.hasOwn(body, "refused_rows")) return { state: "UNSERVED" };
  const map = (body as { refused_rows: unknown }).refused_rows;
  if (!map || typeof map !== "object" || Array.isArray(map))
    throw new Error(`REFUSED_ROWS_MALFORMED: ${String(map)}`);
  const entries = Object.entries(map);
  if (entries.length === 0) return { state: "NONE" };
  const items = entries.map(([key, count]) => {
    if (!/^(strike|price|call_gamma_oi|put_gamma_oi|net):(NULL|BLANK|NOT_NUMERIC|NON_FINITE)$/.test(key))
      throw new Error(`REFUSED_ROWS_MALFORMED: ${key}`);
    if (typeof count !== "number" || !Number.isInteger(count) || count <= 0)
      throw new Error(`REFUSED_ROWS_MALFORMED: ${key}=${String(count)}`);
    const [field, reason] = key.split(":");
    return { field, reason, count };
  });
  items.sort((a, b) => b.count - a.count || (`${a.field}:${a.reason}` < `${b.field}:${b.reason}` ? -1 : 1));
  return { state: "REFUSED", total: items.reduce((sum, item) => sum + item.count, 0), items };
}

/** R4: a fault carries the served map; malformed evidence remains a named fault. */
export function refusedRowsFault(error: unknown): string {
  const map = error && typeof error === "object" ? (error as { refusedRows?: unknown }).refusedRows : undefined;
  if (map === undefined) return "";
  try {
    const rows = readRefusedRows({ refused_rows: map });
    return rows.state === "REFUSED"
      ? ` · REFUSED ${rows.items.map((item) => `${item.field}:${item.reason} ${item.count}`).join(", ")}`
      : "";
  } catch (error) {
    return ` · ${error instanceof Error ? error.message : String(error)}`;
  }
}

/** R3 (review r1 P2): one line naming the door and every item, for a view whose glyph is not ours. */
export function refusedRowsDetail(door: string, rows: { state: string }, drawn: string): string {
  if (rows.state !== "REFUSED") return "";
  const items = (rows as Extract<RefusedRows, { state: "REFUSED" }>).items;
  return `${door} · ${items.map((item) => `${item.field}:${item.reason} · ${item.count} dropped before the ${drawn} was drawn`).join("; ")}`;
}
